import React from 'react';
import PropTypes from 'prop-types';

import BeforeDisplayErrorBoundary from './BeforeDisplayErrorBoundary';
import Message from './Message';
import ConnectNotification from './ConnectNotification';
import ReplyActions from './ReplyActions';
import TypingIndicator from './TypingIndicator';
import TimestampHeader from './TimestampHeader';

import { assignGroups, extractReplyActions } from '../reducers/conversations';
import { delegate } from '../utils/delegate';

const propTypes = {
    conversation: PropTypes.object.isRequired,
    connectNotificationTimestamp: PropTypes.number,
    errorNotificationMessage: PropTypes.string,
    config: PropTypes.object.isRequired,
    text: PropTypes.object.isRequired,
    typingIndicatorShown: PropTypes.bool.isRequired,
    typingIndicatorName: PropTypes.string,
    replyActionsOriginal: PropTypes.array.isRequired,
    replyActionsParentMessage: PropTypes.object.isRequired,
    prechatCaptureMessages: PropTypes.array.isRequired,
    getLastNodeRef: PropTypes.func.isRequired,
    getlastTopMessageNodeRef: PropTypes.func.isRequired,
    getTopMessageNodeRef: PropTypes.func.isRequired,
    getMessagesRef: PropTypes.func.isRequired,
    onMessageLoad: PropTypes.func.isRequired,
    lastTopMessageId: PropTypes.string,
};

export default class ConversationComponent extends React.PureComponent {
    static propTypes = propTypes;

    getReplyActionsNode(replyActions) {
        const { getLastNodeRef } = this.props;
        const choices = replyActions.map(({ text, iconUrl, type, metadata, payload }) => {
            return {
                text,
                iconUrl,
                metadata,
                payload,
                type,
            };
        });

        const refCallback = getLastNodeRef;

        return choices.length > 0 ? <ReplyActions ref={refCallback} choices={choices} /> : null;
    }

    render() {
        const {
            config,
            connectNotificationTimestamp,
            conversation,
            replyActionsOriginal,
            replyActionsParentMessage,
            typingIndicatorName,
            typingIndicatorShown,
            getLastNodeRef,
            getlastTopMessageNodeRef,
            getTopMessageNodeRef,
            prechatCaptureMessages,
            getMessagesRef,
            onMessageLoad,
        } = this.props;

        const { messages } = conversation;
        const {
            colors: { accentColor, linkColor },
        } = config;

        let originalMessages = messages;
        let modifiedMessages = messages;

        if (delegate.hasBeforeDisplay()) {
            const reduced = messages.reduce(
                (acc, message) => {
                    // reply action messages have their own beforeDisplay delegate processing when injected in the props, so avoid duplicated processing
                    const modifiedMessage =
                        message._id && message._id === replyActionsParentMessage._id
                            ? replyActionsParentMessage._isDeletedByBeforeDisplayDelegate
                                ? null
                                : replyActionsParentMessage
                            : delegate.beforeDisplay(message, conversation);

                    if (modifiedMessage) {
                        acc.originalMessages.push(message);
                        acc.modifiedMessages.push(modifiedMessage);
                    }

                    return acc;
                },
                { originalMessages: [], modifiedMessages: [] },
            );

            originalMessages = reduced.originalMessages;
            modifiedMessages = reduced.modifiedMessages;

            assignGroups(originalMessages);
            assignGroups(modifiedMessages);
        }

        const messageItems = modifiedMessages.reduce((items, modifiedMessage, index) => {
            const isLastItem = index === modifiedMessages.length - 1;
            const originalMessage = originalMessages[index];

            const refCallback = (c) => {
                if (index === 0) {
                    getTopMessageNodeRef(c);
                }

                if (this.props.lastTopMessageId === originalMessage._id) {
                    getlastTopMessageNodeRef(c);
                }

                if (isLastItem) {
                    getLastNodeRef(c);
                }
            };

            const isLastInGroup = (msg) => {
                let lastInGroup = msg.lastInGroup;

                if (isLastItem && msg.role !== 'appUser' && typingIndicatorShown && msg.name === typingIndicatorName) {
                    lastInGroup = false;
                }

                return lastInGroup;
            };

            if (originalMessage.firstMessageOfDay) {
                items.push({
                    component: (
                        <TimestampHeader
                            value={originalMessage._clientSent || modifiedMessage.received}
                            key={`ts-header-${originalMessage.received}`}
                        />
                    ),
                });
            }

            const messageProps = {
                messageRef: refCallback,
                accentColor,
                hasFormAfter: !isLastItem && modifiedMessages[index + 1].type === 'form',
                hasFormBefore: index !== 0 && modifiedMessages[index - 1].type === 'form',
                linkColor,
                onLoad: onMessageLoad,
                showStatusIndicator: isLastItem,
            };

            const getMessageNode = (msg) => <Message {...messageProps} {...msg} lastInGroup={isLastInGroup(msg)} />;

            items.push({
                message: originalMessage,
                component: (
                    <BeforeDisplayErrorBoundary
                        key={originalMessage._clientId || originalMessage._id}
                        isMessageModifiedBeforeDisplay={!!modifiedMessage._isModifiedBeforeDisplay}
                        fallbackMessageNode={getMessageNode(originalMessage)}
                    >
                        {getMessageNode(modifiedMessage)}
                    </BeforeDisplayErrorBoundary>
                ),
            });

            return items;
        }, []);

        const replyActions = extractReplyActions(this.props.replyActionsParentMessage);
        if (replyActions.length > 0) {
            messageItems.push({
                component: (
                    <BeforeDisplayErrorBoundary
                        key="reply"
                        isMessageModifiedBeforeDisplay={!!replyActionsParentMessage._isModifiedBeforeDisplay}
                        fallbackMessageNode={this.getReplyActionsNode(replyActionsOriginal)}
                    >
                        {this.getReplyActionsNode(replyActions)}
                    </BeforeDisplayErrorBoundary>
                ),
            });
        }

        if (connectNotificationTimestamp) {
            let isConnectNotificationDirectlyBeforeOfficeHoursMessage = false;
            let connectNotificationIndex = messageItems.findIndex(({ message }) => {
                if (!message) {
                    return false;
                }

                const isFirstMessageAfterNotification = message._clientSent
                    ? message._clientSent > connectNotificationTimestamp
                    : message.received > connectNotificationTimestamp;

                const isOfficeHoursMessage = message.source && message.source.type === 'officehours';

                if (isFirstMessageAfterNotification && isOfficeHoursMessage) {
                    isConnectNotificationDirectlyBeforeOfficeHoursMessage = true;
                }

                return isFirstMessageAfterNotification;
            });

            /*
             * we want to ensure that the connect notification appears after
             * the Office Hours message if the Office Hours message is the
             * first message after the connect notification
             */
            if (isConnectNotificationDirectlyBeforeOfficeHoursMessage) {
                connectNotificationIndex++;
            }
            if (connectNotificationIndex > -1) {
                messageItems.splice(connectNotificationIndex, 0, {
                    component: <ConnectNotification key="connect-notification" />,
                });
            } else {
                messageItems.push({ component: <ConnectNotification key="connect-notification" /> });
            }
        }

        if (config.prechatCapture && config.prechatCapture.enabled && messageItems.length === 0) {
            messageItems.push(...prechatCaptureMessages);
        }

        if (typingIndicatorShown) {
            let firstInGroup = true;

            if (originalMessages.length > 0) {
                const lastMessage = originalMessages[originalMessages.length - 1];
                if (lastMessage.role !== 'appUser' && lastMessage.name === typingIndicatorName) {
                    firstInGroup = false;
                }
            }

            messageItems.push({
                component: <TypingIndicator ref={getLastNodeRef} firstInGroup={firstInGroup} key="typing-indicator" />,
            });
        }

        return (
            <div ref={getMessagesRef} className="messages">
                {messageItems.map(({ component }) => component)}
            </div>
        );
    }
}
